import styled from 'styled-components';
import { theme } from 'styled-tools';

import Box from '@ui/box/Box.tsx';
import { Icon } from '@ui/icons';

import RouteSegments from './RouteSegments.tsx';
import type { RouteNotice, RouteSummaryProps } from './types.ts';

const START_BUTTON_HEIGHT = 40;
// Preserve the original metric and notice wrapping within the desktop card.
const CARD_SPACING = { padding: 13, detailRows: 2, noticeGap: 8 };
const NOTICE_ICON_OVERHANG = { block: 2, inline: 6 };

const Summary = styled.section`
  width: 100%;
  font-family: ${theme('fonts.family')};
  color: ${theme('colors.primaryText')};
`;

const Card = styled(Box)`
  width: 100%;
  padding: ${CARD_SPACING.padding}px;
  border: ${theme('borderWidths.sm')} solid ${theme('colors.secondaryButtonHover')};
  border-radius: ${theme('radii.lg')};
  background-color: ${theme('colors.widgetBackground')};
`;

const Details = styled.div`
  flex: 1;
  min-width: 0;
`;

const Duration = styled.div`
  font-size: ${theme('fontSizes.xl')};
  font-weight: ${theme('fontWeights.normal')};
  line-height: 1.3;
  overflow-wrap: anywhere;
`;

const RouteStats = styled.div`
  margin-block-start: ${CARD_SPACING.detailRows}px;
  color: ${theme('colors.secondaryText')};
  font-size: ${theme('fonts.size.h4')};
  font-weight: ${theme('fontWeights.lighter')};
  line-height: 1.5;
  overflow-wrap: anywhere;
`;

const Notice = styled.div`
  display: flex;
  align-items: center;
  gap: ${CARD_SPACING.noticeGap}px;
  margin-block-start: ${CARD_SPACING.detailRows}px;
  color: ${theme('colors.secondaryText')};
  font-size: ${theme('fonts.size.h4')};
  font-weight: ${theme('fontWeights.lighter')};
  line-height: 1.5;
  overflow-wrap: anywhere;
`;

const NoticeIcon = styled(Icon)<{ $kind: RouteNotice['kind'] }>`
  flex: none;
  margin: -${NOTICE_ICON_OVERHANG.block}px -${NOTICE_ICON_OVERHANG.inline}px;
  color: ${({ $kind, theme: currentTheme }) =>
    $kind === 'warning' ? currentTheme.colors.statusErrorBackground : currentTheme.colors.statusActiveBackground};

  path {
    fill: currentColor;
  }
`;

const StartButton = styled(Box)`
  flex: none;
  min-height: ${START_BUTTON_HEIGHT}px;
  padding-block: 0;
  border: 0;
  border-radius: ${theme('radii.lg')};
  background-color: ${theme('colors.primaryButton')};
  color: ${theme('colors.primaryButtonText')};
  font: inherit;
  font-size: ${theme('fonts.size.h4')};
  font-weight: ${theme('fontWeights.normal')};
  line-height: 1.3;
  cursor: pointer;

  &:hover {
    background-color: ${theme('colors.primaryButtonHover')};
  }

  &:focus-visible {
    outline: ${theme('borderWidths.md')} solid ${theme('colors.focusOutlineColor')};
    outline-offset: ${theme('borderWidths.md')};
  }
`;

const Separator = () => <span aria-hidden="true"> &bull; </span>;

const StopCount = styled.span`
  white-space: nowrap;
`;

export default function RouteSummary({
  T,
  duration,
  eta,
  distance,
  segments,
  routeNotice,
  destination,
  destinationIcon,
  onStart,
}: RouteSummaryProps) {
  const stopCount = segments.filter(segment => segment.kind === 'stop').length;

  return (
    <Summary aria-label={T('getDirectionsFromTo:Route summary')}>
      <Card display="flex" alignItems="center" gridGap={3}>
        <Details>
          <Duration>{duration}</Duration>
          <RouteStats>
            <span>{eta}</span>
            <Separator />
            <span>{distance}</span>
            {stopCount > 0 && (
              <>
                <Separator />
                <StopCount>{T('getDirectionsFromTo:_count_ stop', { count: stopCount })}</StopCount>
              </>
            )}
          </RouteStats>
          <div role="status">
            {routeNotice && (
              <Notice>
                <NoticeIcon
                  $kind={routeNotice.kind}
                  name={routeNotice.kind === 'warning' ? 'triangle.warning' : 'accessibility'}
                  iconSize="md"
                  aria-hidden="true"
                />
                {routeNotice.message}
              </Notice>
            )}
          </div>
        </Details>
        <StartButton
          as="button"
          type="button"
          px={3}
          aria-label={
            destination ? T('getDirectionsFromTo:Start navigation to _location_', { location: destination }) : undefined
          }
          onClick={onStart}
        >
          {T('getDirectionsFromTo:Start')}
        </StartButton>
      </Card>
      <RouteSegments T={T} segments={segments} destinationIcon={destinationIcon} />
    </Summary>
  );
}
